import { Check } from 'lucide-react';
import { cn } from '@/lib/cn';
import { AuthPosDemo } from './AuthPosDemo';

/**
 * The panel beside the sign-in form.
 *
 * It holds a WORKING TILL, not a picture of one. See AuthPosDemo — tapping a
 * tile rings it up, GST breaks out of the total, Charge clears the sale.
 * Somebody deciding whether to sign up gets to use the product before handing
 * over an email address.
 *
 * It replaced a takings figure, a bar chart and a sparkline. Those were three
 * invented numbers dressed as evidence: nobody believes a screenshot's
 * ₹18,240, and a chart of made-up data is decoration that costs the same
 * space as a demo.
 *
 * THE PANEL ITSELF NO LONGER MOVES. There was a pointer parallax here that
 * drifted the background one way and the cards the other. It was pleasant over
 * static cards and wrong over buttons — ambient motion under something you are
 * meant to aim at is a moving target, and the drift fought every tap. Entrance
 * animation only now.
 *
 * The background is entirely CSS — no image files. A gradient PNG large enough
 * not to band is a few hundred kilobytes on the one screen that must paint
 * before anybody has an account; this also stays on palette when a token
 * changes, and renders on a shop's flaky connection.
 *
 * Near-black with saturated green light through it, because #0b0a1c is the
 * app's OWN chrome — what somebody sees here is the sidebar they get thirty
 * seconds after signing in, which is a better promise than a pastel.
 *
 * The blooms are TIGHT and near-opaque at the core. Spread thin over black
 * they turn olive: a green at 0.3 alpha on #0b0a1c is not a dim green, it is a
 * grey. Saturation has to live in a small bright centre and fall away fast.
 */
export function AuthShowcase() {
  return (
    <div
      className={cn(
        'relative hidden overflow-hidden lg:block',
        // STICKY, so the till stays put while a long form scrolls past it.
        // /login runs to about 900px once the demo accounts are listed, and on
        // a laptop that scrolled the one interactive thing on the page off the
        // top.
        //
        // `h-dvh` rather than the stretched grid height is what makes it work
        // at all: a sticky box taller than the viewport has nothing to stick
        // to, and a grid item only escapes `align-self: stretch` once its
        // height is definite. `self-start` says so out loud.
        'lg:sticky lg:top-0 lg:h-dvh lg:self-start',
      )}
    >
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(42% 34% at 24% 20%, rgba(80,235,140,0.95) 0%, rgba(66,213,119,0.35) 45%, rgba(66,213,119,0) 72%),
            radial-gradient(38% 30% at 84% 14%, rgba(126,240,168,0.55) 0%, rgba(126,240,168,0) 70%),
            radial-gradient(62% 50% at 94% 74%, rgba(16,163,92,0.85) 0%, rgba(10,125,69,0.25) 50%, rgba(10,125,69,0) 76%),
            radial-gradient(46% 38% at 6% 90%, rgba(66,213,119,0.55) 0%, rgba(66,213,119,0) 72%),
            radial-gradient(30% 26% at 55% 52%, rgba(126,240,168,0.20) 0%, rgba(126,240,168,0) 70%),
            linear-gradient(155deg, #0d1f18 0%, #0b0a1c 48%, #08251a 100%)
          `,
        }}
      />

      {/* Grain. Without it the blooms band visibly on the kind of monitor a
          shop counter actually has. Soft-light keeps it as texture on a dark
          field rather than a grey haze over one. */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.22] mix-blend-soft-light"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='140' height='140'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/*
        Centred with `m-auto` on the child rather than `justify-center` on the
        parent. They look identical until the panel is shorter than its
        contents — around 640px of viewport height — and then `justify-center`
        overflows in BOTH directions and puts the top of the intro somewhere
        no scrollbar can reach. Auto margins resolve to zero when there is no
        free space, so the content simply starts at the top and scrolls.
      */}
      <div className="scroll-slim-dark relative flex h-full overflow-y-auto p-10 xl:p-12">
        <div className="m-auto flex w-full flex-col gap-7">
          <Intro />
          <AuthPosDemo />
        </div>
      </div>
    </div>
  );
}

function Intro() {
  return (
    <div className="auth-rise max-w-lg" style={{ animationDelay: '60ms' }}>
      <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#7ef0a8]">
        Built for Indian shops
      </p>

      <h2 className="mt-3 text-[26px] font-semibold leading-[1.15] tracking-[-0.02em] text-white xl:text-[30px]">
        Your counter and your books, on one screen.
      </h2>

      <ul className="mt-4 flex flex-col gap-2">
        {[
          'Barcode billing, split payments, GST on every receipt',
          'Stock that reconciles to the last unit, across every till',
          'Reports and a ledger that agree with your bank',
        ].map((line, index) => (
          <li
            key={line}
            className="auth-rise flex items-start gap-2.5 text-sm leading-snug text-white/75"
            style={{ animationDelay: `${140 + index * 70}ms` }}
          >
            <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-logo/20 text-[#7ef0a8]">
              <Check className="h-2.5 w-2.5" strokeWidth={3.5} aria-hidden />
            </span>
            {line}
          </li>
        ))}
      </ul>
    </div>
  );
}
